export type DocsNavItem = {
  /** Translation key under the docs.nav namespace (e.g. "items.welcome"). */
  labelKey: string;
  href: string;
  /** When true, this item appears in sidebar but is skipped by prev/next walking. */
  external?: boolean;
  /**
   * When true, the page is gated "coming soon" for launch: it is dropped from the
   * sidebar, prev/next walking, the overview cards, and the sitemap, and the route
   * itself 404s (see `isHiddenDoc`). The page's .tsx file is kept in the repo as
   * reference. To ship the feature, delete this flag here, that single change
   * re-enables the nav entry, sitemap URL, and the live route together.
   */
  hidden?: boolean;
};

export type DocsNavSection = {
  /** Translation key under the docs.nav namespace (e.g. "sections.getStarted"). */
  labelKey: string;
  items: DocsNavItem[];
};

export const docsNav: DocsNavSection[] = [
  {
    labelKey: "sections.getStarted",
    items: [
      { labelKey: "items.welcome", href: "/docs" },
      { labelKey: "items.quickstart", href: "/docs/quickstart" },
      { labelKey: "items.concepts", href: "/docs/concepts" },
      { labelKey: "items.whatsNew", href: "/changelog", external: true },
    ],
  },
  {
    labelKey: "sections.sources",
    items: [
      { labelKey: "items.sourcesOverview", href: "/docs/sources" },
      { labelKey: "items.tradingview", href: "/docs/sources/tradingview" },
      { labelKey: "items.telegram", href: "/docs/sources/telegram", hidden: true },
      { labelKey: "items.restApi", href: "/docs/sources/rest-api", hidden: true },
      { labelKey: "items.manual", href: "/docs/sources/manual" },
    ],
  },
  {
    labelKey: "sections.destinations",
    items: [
      { labelKey: "items.destinationsOverview", href: "/docs/targets" },
      { labelKey: "items.mt5", href: "/docs/targets/mt5" },
      { labelKey: "items.telegramChannel", href: "/docs/targets/telegram-channel", hidden: true },
      { labelKey: "items.discordWebhook", href: "/docs/targets/discord", hidden: true },
      { labelKey: "items.httpWebhook", href: "/docs/targets/http-webhook" },
    ],
  },
  {
    labelKey: "sections.flowsRules",
    items: [
      { labelKey: "items.whatIsAFlow", href: "/docs/routes" },
      { labelKey: "items.buildingAFlow", href: "/docs/routes/building" },
      { labelKey: "items.ruleCatalog", href: "/docs/routes/steps" },
    ],
  },
  {
    labelKey: "sections.reference",
    items: [
      { labelKey: "items.signalFormat", href: "/docs/reference/signal-format" },
      { labelKey: "items.planLimits", href: "/docs/reference/plan-limits" },
      { labelKey: "items.symbolMapping", href: "/docs/reference/symbol-mapping" },
    ],
  },
  {
    labelKey: "sections.configuration",
    items: [
      { labelKey: "items.language", href: "/docs/configuration/language" },
    ],
  },
];

/** Hrefs of pages gated "coming soon" for launch (see `DocsNavItem.hidden`). */
const hiddenHrefs = new Set(
  docsNav.flatMap((s) => s.items).filter((i) => i.hidden).map((i) => i.href),
);

/**
 * True when a docs route is currently gated "coming soon" and should 404.
 * Route pages call this at the top of their component; nav, overview cards, and
 * the sitemap all filter on the same set, so one `hidden` flag drives everything.
 */
export function isHiddenDoc(pathname: string): boolean {
  return hiddenHrefs.has(pathname);
}

/** Sidebar view of the nav with hidden items (and now-empty sections) removed. */
export const visibleDocsNav: DocsNavSection[] = docsNav
  .map((section) => ({ ...section, items: section.items.filter((i) => !i.hidden) }))
  .filter((section) => section.items.length > 0);

/** Flat list of in-docs pages (excludes external entries like /changelog and hidden pages). */
const flatDocs: DocsNavItem[] = docsNav
  .flatMap((s) => s.items)
  .filter((i) => !i.external && !i.hidden);

export function getPrevNext(pathname: string): { prev?: DocsNavItem; next?: DocsNavItem } {
  const idx = flatDocs.findIndex((i) => i.href === pathname);
  if (idx === -1) return {};
  return {
    prev: idx > 0 ? flatDocs[idx - 1] : undefined,
    next: idx < flatDocs.length - 1 ? flatDocs[idx + 1] : undefined,
  };
}

/**
 * Returns the breadcrumb trail for a pathname as translation keys
 * (section labelKey, page labelKey). Both resolve under docs.nav.
 */
export function getBreadcrumb(pathname: string): { section: string; page: string } | undefined {
  for (const section of docsNav) {
    const item = section.items.find((i) => i.href === pathname);
    if (item) return { section: section.labelKey, page: item.labelKey };
  }
  return undefined;
}
